import { z } from "zod";

/**
 * Clinic settings registry. Each setting has a schema and a default.
 * Stored per clinic in `clinic_setting` as JSON; anything not stored uses the default.
 * Money is always whole cents.
 */

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex color like #C51A6A");
const cents = z.number().int().min(0).max(10_000_000);
const percent = z.number().min(0).max(100);
const text = (max: number) => z.string().trim().max(max);

/** Contexts a booking can happen in. */
export const BOOKING_CONTEXTS = ["clinic", "mobile", "event"] as const;
export type BookingContext = (typeof BOOKING_CONTEXTS)[number];

/** How a deposit is figured: a flat amount per guest, or a percent of the total. */
export const depositRuleSchema = z
  .object({
    mode: z.enum(["flat_per_guest", "percent"]),
    flatCents: cents,
    percent,
    minCents: cents.nullable(),
    maxCents: cents.nullable(),
  })
  .refine((r) => r.minCents === null || r.maxCents === null || r.minCents <= r.maxCents, "Minimum can't be more than maximum");
export type DepositRule = z.infer<typeof depositRuleSchema>;

/** Percent off by group size. Tiers are sorted by minGuests; the highest tier reached applies. */
export const groupDiscountTiersSchema = z
  .array(z.object({ minGuests: z.number().int().min(2).max(100), percent }))
  .max(10)
  .refine((t) => new Set(t.map((x) => x.minGuests)).size === t.length, "Each tier needs a different group size");
export type GroupDiscountTier = z.infer<typeof groupDiscountTiersSchema>[number];

/** Paper sizes for the printed menu, in inches (portrait). */
export const PRINT_PAPERS = {
  "13x19": { label: "13 × 19 in", width: 13, height: 19 },
  "11x17": { label: "11 × 17 in", width: 11, height: 17 },
  letter: { label: "Letter (8.5 × 11 in)", width: 8.5, height: 11 },
  legal: { label: "Legal (8.5 × 14 in)", width: 8.5, height: 14 },
} as const;
export type PrintPaper = keyof typeof PRINT_PAPERS;

/** A dosing table printed under the menu (e.g. NAD+ by route). */
export const printNoteSchema = z.object({
  title: text(80).min(1),
  rows: z.array(z.object({ route: text(40), dose: text(200), freq: text(40), notes: text(300) })).max(20),
});
export type PrintNote = z.infer<typeof printNoteSchema>;

/** One printed sheet: a paper size and the categories on it. */
export const printSheetSchema = z.object({
  id: z.string().uuid(),
  name: text(60).min(1),
  paper: z.enum(Object.keys(PRINT_PAPERS) as [PrintPaper, ...PrintPaper[]]),
  landscape: z.boolean(),
  columns: z.number().int().min(1).max(4),
  categoryIds: z.array(z.string().uuid()).max(40),
  /** Also print items hidden from the public menu. */
  includeHidden: z.boolean(),
  /** List what's in a mix (B-Complex, Lipo-Mino) under it. Unset: only on sheets with one-per-visit categories (drips). */
  showMixes: z.boolean().optional(),
  /** Spread the boxes over the columns evenly (each column in menu order) instead of flowing in order. */
  arrange: z.boolean().optional(),
  notes: z.array(printNoteSchema).max(5),
});
export type PrintSheet = z.infer<typeof printSheetSchema>;

/** A special on the lobby display: a ribbon, a headline, a line, and (optional) a plan or service it opens. */
export const lobbySpecialSchema = z.object({
  id: z.string().min(1).max(40),
  ribbon: z.string().trim().max(40),
  headline: z.string().trim().min(1, "Give each special a headline").max(120),
  text: z.string().trim().max(300).default(""),
  link: z.object({ kind: z.enum(["plan", "service"]), id: z.string().uuid() }).nullable().default(null),
  active: z.boolean().default(true),
});
export type LobbySpecial = z.infer<typeof lobbySpecialSchema>;

/** Artwork slots for the lobby's animated menu (docs/lobby-signage.md), each an uploaded image. */
export const SIGNAGE_ART_SLOTS = [
  "logo", "logo-mark", "brand-bg", "brand-light", "iv-bg", "iv-bag", "droplets", "drip-hydration", "drip-immune", "drip-performance", "drip-beauty", "drip-recovery",
  "menu-bg", "recovery-bg", "athlete", "boost-bg", "vial", "fountain-bg", "vault-bg", "special-bg", "lounge-bg",
] as const;
export type SignageArtSlot = (typeof SIGNAGE_ART_SLOTS)[number];
export const SIGNAGE_ART_LABELS: Record<SignageArtSlot, string> = {
  logo: "Logo (white, for the intro and Book)",
  "logo-mark": "Small logo (white, corner of each scene)",
  "brand-bg": "Intro background",
  "brand-light": "Intro light streak (transparent)",
  "iv-bg": "Lounge haze background",
  "iv-bag": "IV bag (transparent)",
  droplets: "Droplets (transparent)",
  "drip-hydration": "Hydration",
  "drip-immune": "Immune",
  "drip-performance": "Performance",
  "drip-beauty": "Beauty",
  "drip-recovery": "Recovery (resting)",
  "menu-bg": "Menu board background",
  "recovery-bg": "Compression recovery",
  athlete: "Athlete (transparent)",
  "boost-bg": "Vials row",
  vial: "Single vial (transparent)",
  "fountain-bg": "Rising particles",
  "vault-bg": "Vault door (memberships)",
  "special-bg": "Specials background",
  "lounge-bg": "Lounge (Book now)",
};
const artSlot = z.enum(SIGNAGE_ART_SLOTS);
/** The lobby's animated menu: which services get their own scene (with their picture), what's left off the menu boards, and the artwork. */
export const lobbySignageSchema = z.object({
  enabled: z.boolean(),
  featured: z.array(z.object({ serviceId: z.string().uuid(), art: artSlot.nullable() })).max(20),
  hidden: z.array(z.string().uuid()).max(300),
  /** Each menu board's picture (by category name), drawn beside the list. */
  boardArt: z.record(z.string().max(80), artSlot.nullable()),
  art: z.partialRecord(artSlot, z.string().max(300)),
});
export type LobbySignage = z.infer<typeof lobbySignageSchema>;

export const settingsSchema = z.object({
  /** The lobby display's screen saver video (stored file), and its specials. */
  "lobby.videoKey": z.string().max(300).nullable(),
  "lobby.specials": z.array(lobbySpecialSchema).max(10),
  "lobby.signage": lobbySignageSchema,
  "security.inactivityTimeoutMinutes": z.number().int().min(15).max(720),
  "branding.primaryColor": hexColor,
  "branding.accentColor": hexColor,
  "branding.logoKey": z.string().max(500).nullable(),

  // Public contact details. Each can be kept internal (stored, not shown publicly).
  "profile.phone": text(30).nullable(),
  "profile.phonePublic": z.boolean(),
  "profile.email": z.string().trim().email().max(254).nullable(),
  "profile.emailPublic": z.boolean(),
  "profile.website": z.string().trim().url().max(300).nullable(),
  /** In the footer of client emails. */
  "profile.instagram": z.string().trim().url().max(300).nullable(),
  "profile.facebook": z.string().trim().url().max(300).nullable(),
  "profile.tagline": text(120).nullable(),
  /** For the clinic's website feed: street address, and opening hours as written ("Mon–Fri 9–6\nSat 10–2"). */
  "profile.address": text(200).nullable(),
  "profile.hours": text(400).nullable(),

  // Bookings and payments (enforced from the booking and payment steps on).
  "booking.groupMinGuests": z.number().int().min(2).max(50),
  "booking.clientsChooseNurse": z.boolean(),
  /** Clinic visits (online and staff-made) stay unconfirmed until the client replies C. */
  "booking.clientConfirms": z.boolean(),
  /** Good faith exams through Qualiphy. */
  "gfe.qualiphyEnabled": z.boolean(),
  /** Record purchase can read an uploaded invoice with AI (Claude through Amazon Bedrock). */
  "inventory.invoiceReading": z.boolean(),
  /** Charts: "AI Cleanup" under the provider narrative (Claude through Amazon Bedrock). */
  "charts.aiCleanup": z.boolean(),
  /** RN payouts: the commission percent unless a pay rate says otherwise. */
  "payouts.defaultPercent": percent,
  /** Pay periods; weekly and every-two-weeks count from payouts.anchorDate (a period's first day). */
  "payouts.schedule": z.enum(["semimonthly", "monthly", "biweekly", "weekly"]),
  "payouts.anchorDate": z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** The default Qualiphy exam (first ID), for services set to "Clinic default". */
  "gfe.qualiphyExamIds": z.array(z.number().int().positive()).max(5),
  /** How long an approval (or a medical director tag) counts. */
  "gfe.approvalMonths": z.number().int().min(1).max(36),
  /** Chart types the medical director must review once the nurse signs (Pending Charts). */
  "charts.mustReview": z.array(z.enum(["iv", "im", "adverse"])).max(3),
  /**
   * Staging only: the exam every invite uses instead (e.g. #5359, Qualiphy's no-charge test exam), in
   * Qualiphy's test state with the name "Test Approve"; no real provider or patient. Null = off.
   */
  "gfe.testExamId": z.number().int().positive().nullable(),
  /** Auto-send is paused until this time (it resumes at the next midnight). */
  "gfe.autoSendPausedUntil": z.string().datetime().nullable(),
  /** Tags set from Qualiphy results (null = none). */
  "gfe.resultTags": z.object({
    approved: z.string().uuid().nullable(),
    rejected: z.string().uuid().nullable(),
    deferred: z.string().uuid().nullable(),
    flagged: z.string().uuid().nullable(),
  }),
  /**
   * Which approvals need a person to read them: the provider's order questions (matched by wording)
   * whose answer doesn't start with one of the unrestricted answers.
   */
  "gfe.reviewRules": z.object({
    orderQuestionContains: z.array(text(120)).max(10),
    unrestrictedAnswers: z.array(text(300)).max(10),
  }),
  /** Per exam (by ID): the answer sent for the visit's reason, e.g. "Dehydration Support". No entry = let the client answer. */
  "gfe.reasonAnswers": z.record(z.string().regex(/^\d{1,9}$/), z.object({ questionId: z.number().int(), responseId: z.number().int(), label: z.string().max(200) })),
  "booking.maxAdvanceDays": z.number().int().min(1).max(365),
  /** The availability calendar shows these hours (it stretches to fit anything outside them). "HH:MM", clinic time. */
  "availability.viewStart": z.string().regex(/^([01]\d|2[0-3]):00$/),
  "availability.viewEnd": z.string().regex(/^([01]\d|2[0-3]):00$|^24:00$/),
  "booking.clinicMinNoticeMinutes": z.number().int().min(0).max(10_080),
  "booking.slotMinutes": z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20), z.literal(30), z.literal(60)]),
  "payments.clinicDepositEnabled": z.boolean(),
  "payments.clinicDepositCents": cents,
  "payments.holdEnabled": z.boolean(),
  "payments.holdPercent": z.number().int().min(100).max(300),
  "payments.groupDeposit": depositRuleSchema,
  "payments.mobileDeposit": depositRuleSchema,
  "payments.eventDeposit": depositRuleSchema,
  "payments.chipInDeadlineHours": z.number().int().min(1).max(168),
  "payments.groupDepositCredit": z.enum(["booker", "split"]),
  /** Clinic bookings save the client's card (for the no-show / late-cancel fee and checkout). */
  "payments.cardOnFileClinic": z.boolean(),
  /** With a card on file on: online bookings don't wait for it (the fee policy still applies; adding one is offered after booking). */
  "payments.cardOptional": z.boolean(),
  /** Charged to the card on file for clinic no-shows and cancellations inside the window; 0 = none. */
  "payments.noShowFeeCents": cents,
  /** Suggested tips at checkout, in percent of the full amount. */
  /** Suggested tips on every tip screen (staff checkout, iPad, card reader), with Custom and No tip. Stripe's readers show up to 3. */
  "payments.tipPercents": z.array(z.number().int().min(1).max(100)).min(1, "Add at least one tip percentage").max(3),
  /** Tap payments on a Stripe card reader. */
  "payments.cardReaderEnabled": z.boolean(),
  /** The way to pay Checkout starts on (when it's available for that visit). */
  "payments.checkoutStartsOn": z.enum(["card_on_file", "card", "card_present", "cash", "link", "ipad"]),
  /** The card readers' idle-screen picture (set through Settings → Payments, which sends it to Stripe). */
  "payments.readerBackgroundKey": z.string().max(500).nullable(),
  /** Bringing data over from Boulevard (Settings → Boulevard); turned off once the clinic has left Boulevard. */
  "boulevard.enabled": z.boolean(),
  "pricing.groupDiscountClinic": groupDiscountTiersSchema,
  "pricing.groupDiscountMobile": groupDiscountTiersSchema,
  "pricing.groupDiscountEvent": groupDiscountTiersSchema,

  // Cancellations and no-shows.
  "cancellation.windowHours": z.number().int().min(0).max(168),
  "cancellation.policyText": text(2000),

  // Client messages: scheduled ones wait outside quiet hours (clinic time, "HH:MM").
  "messages.quietStart": z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  "messages.quietEnd": z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  /** Client email comes from this address (its domain must be verified in Amazon SES); none = no email. */
  "messages.emailFrom": z.string().trim().email().max(254).nullable(),
  /** The printed nurse menu (Catalog → Print menu): which categories go on which sheet. */
  "menu.printSheets": z.array(printSheetSchema).max(6),
  /** Keep every named number (clients, and named non-clients) in the texting user's RingCentral contacts. */
  "phone.contactsSync": z.boolean(),

  // Mobile visits.
  "mobile.leadTimeMinutes": z.number().int().min(0).max(10_080),
  "mobile.manualConfirmation": z.boolean(),
  "mobile.locationTypes": z
    .array(
      z.object({
        key: z.string().regex(/^[a-z0-9_]{1,40}$/),
        label: text(80).min(1),
        knownClientsOrGroupsOnly: z.boolean(),
        /** Shown when a client picks this kind of place; they tick that they understand. Empty = none. */
        notice: text(1000).default(""),
      }),
    )
    .max(20),
  /** Extra points the service area is measured from (besides the clinic), e.g. the owners' home. Never shown to clients. */
  "mobile.serviceAreaCenters": z.array(z.object({ label: text(60).min(1), address: text(200).min(5) })).max(5),
  /** Online mobile bookings need at least this many guests. */
  "mobile.minGuests": z.number().int().min(1).max(20),
  /** Distance bands from the location, ascending. Beyond the last band is declined. */
  "mobile.serviceAreaBands": z.array(z.object({ maxMiles: z.number().min(1).max(500), feeCents: cents })).min(1).max(10),
  "mobile.travelFeeMaxCents": cents,
  /** Nurses required by group size, ascending. */
  "mobile.nursesByGroupSize": z.array(z.object({ maxGuests: z.number().int().min(1).max(100), nurses: z.number().int().min(1).max(20) })).min(1).max(10),
  "mobile.acknowledgmentText": text(2000),
  /** The nurse is busy this long before and after each mobile visit (driving, setting up). */
  "mobile.travelBeforeMinutes": z.number().int().min(0).max(240),
  "mobile.travelAfterMinutes": z.number().int().min(0).max(240),
  "mobile.maxAdvanceDays": z.number().int().min(1).max(365),
  /**
   * Unanswered requests: nurses are texted right away and every `repeatMinutes`; managers after
   * `managerAfterMinutes` (or that close to the visit); escalation contacts likewise; and the
   * request is cancelled `cancelBeforeVisitMinutes` before the visit.
   */
  "mobile.escalation": z.object({
    repeatMinutes: z.number().int().min(5).max(240),
    managerAfterMinutes: z.number().int().min(5).max(1440),
    managerBeforeVisitMinutes: z.number().int().min(0).max(1440),
    ownerAfterMinutes: z.number().int().min(5).max(1440),
    ownerBeforeVisitMinutes: z.number().int().min(0).max(1440),
    cancelBeforeVisitMinutes: z.number().int().min(0).max(1440),
  }),
  /** Who gets the last escalation texts (staff ids). Empty = everyone who can manage roles (owners). */
  "mobile.escalationStaffIds": z.array(z.string().uuid()).max(10),
});

export type ClinicSettings = z.infer<typeof settingsSchema>;
export type SettingKey = keyof ClinicSettings;

const halfDeposit: DepositRule = { mode: "percent", flatCents: 0, percent: 50, minCents: null, maxCents: null };

/** Shown when a client picks a residence or hotel room for a mobile visit. */
export const RESIDENCE_NOTICE =
  "Residence and hotel visits are reserved for groups and established clients, not for one or two people staying in a hotel room. This is for our nurses' safety. We may decline requests that don't fit.";

export const SETTING_DEFAULTS: ClinicSettings = {
  "security.inactivityTimeoutMinutes": 30,
  "branding.primaryColor": "#1F1A1C",
  "branding.accentColor": "#C51A6A",
  "branding.logoKey": null,

  "profile.phone": null,
  "profile.phonePublic": true,
  "profile.email": null,
  "profile.emailPublic": false,
  "profile.website": null,
  "profile.instagram": null,
  "profile.facebook": null,
  "profile.tagline": null,
  "profile.address": null,
  "profile.hours": null,

  "booking.groupMinGuests": 2,
  "booking.clientsChooseNurse": false,
  "booking.clientConfirms": true,
  "gfe.qualiphyEnabled": false,
  "inventory.invoiceReading": true,
  "charts.aiCleanup": true,
  "payouts.defaultPercent": 50,
  "payouts.schedule": "semimonthly",
  "payouts.anchorDate": "2026-01-05",
  "lobby.videoKey": null,
  "lobby.specials": [],
  "lobby.signage": { enabled: false, featured: [], hidden: [], boardArt: {}, art: {} },
  "gfe.qualiphyExamIds": [],
  "gfe.approvalMonths": 12,
  "charts.mustReview": [],
  "gfe.testExamId": null,
  "gfe.autoSendPausedUntil": null,
  "gfe.resultTags": { approved: null, rejected: null, deferred: null, flagged: null },
  "gfe.reasonAnswers": {},
  "gfe.reviewRules": {
    orderQuestionContains: ["Order and Treatment Recommendation", "Ordered medication"],
    unrestrictedAnswers: ["Patient is approved for IV and IM Nutrient Therapy including ALL ingredients", "Approved for all as needed"],
  },
  "booking.maxAdvanceDays": 30,
  "availability.viewStart": "06:00",
  "availability.viewEnd": "18:00",
  "booking.clinicMinNoticeMinutes": 90,
  "booking.slotMinutes": 15,
  "payments.clinicDepositEnabled": false,
  "payments.clinicDepositCents": 5000,
  "payments.holdEnabled": false,
  "payments.holdPercent": 150,
  "payments.groupDeposit": halfDeposit,
  "payments.mobileDeposit": halfDeposit,
  "payments.eventDeposit": halfDeposit,
  "payments.chipInDeadlineHours": 24,
  "payments.groupDepositCredit": "booker",
  "payments.cardOnFileClinic": true,
  "payments.cardOptional": false,
  "payments.noShowFeeCents": 5000,
  "payments.tipPercents": [15, 20, 25],
  "payments.cardReaderEnabled": false,
  "payments.checkoutStartsOn": "card_on_file",
  "payments.readerBackgroundKey": null,
  "boulevard.enabled": true,
  "pricing.groupDiscountClinic": [],
  "pricing.groupDiscountMobile": [],
  "pricing.groupDiscountEvent": [],

  "cancellation.windowHours": 24,
  "cancellation.policyText":
    "Please give us at least 24 hours' notice to cancel or reschedule. Clinic visits cancelled within 24 hours, and no-shows, are charged a $50 fee to the card on file. Mobile and event deposits are non-refundable.",
  "messages.quietStart": "22:00",
  "messages.quietEnd": "06:00",
  "messages.emailFrom": null,
  "menu.printSheets": [],
  "phone.contactsSync": false,

  "mobile.leadTimeMinutes": 120,
  "mobile.manualConfirmation": true,
  "mobile.locationTypes": [
    { key: "workplace", label: "Workplace", knownClientsOrGroupsOnly: false, notice: "" },
    { key: "business", label: "Business or venue", knownClientsOrGroupsOnly: false, notice: "" },
    { key: "residence", label: "Residence or hotel room", knownClientsOrGroupsOnly: true, notice: RESIDENCE_NOTICE },
  ],
  "mobile.serviceAreaCenters": [],
  "mobile.minGuests": 2,
  "mobile.serviceAreaBands": [{ maxMiles: 25, feeCents: 0 }],
  "mobile.travelFeeMaxCents": 50000,
  "mobile.nursesByGroupSize": [{ maxGuests: 4, nurses: 1 }],
  "mobile.acknowledgmentText":
    "I confirm I have permission to receive IV therapy at this location and that the space is safe and suitable for treatment.",
  "mobile.travelBeforeMinutes": 30,
  "mobile.travelAfterMinutes": 30,
  "mobile.maxAdvanceDays": 60,
  "mobile.escalation": { repeatMinutes: 30, managerAfterMinutes: 60, managerBeforeVisitMinutes: 180, ownerAfterMinutes: 120, ownerBeforeVisitMinutes: 120, cancelBeforeVisitMinutes: 60 },
  "mobile.escalationStaffIds": [],
};

export const SETTING_KEYS = Object.keys(SETTING_DEFAULTS) as SettingKey[];

export function isSettingKey(key: string): key is SettingKey {
  return key in SETTING_DEFAULTS;
}

/** Merge stored values over defaults, dropping anything invalid or unknown. */
export function resolveSettings(stored: Record<string, unknown>): ClinicSettings {
  const out: Record<string, unknown> = { ...SETTING_DEFAULTS };
  for (const key of SETTING_KEYS) {
    if (!(key in stored)) continue;
    const parsed = settingsSchema.shape[key].safeParse(stored[key]);
    if (parsed.success) out[key] = parsed.data;
  }
  return out as ClinicSettings;
}

export const settingsUpdateSchema = settingsSchema.partial();

/** The group discount percent for a group of this size, from tiers. */
export function groupDiscountPercent(tiers: readonly GroupDiscountTier[], guests: number): number {
  let pct = 0;
  for (const t of [...tiers].sort((a, b) => a.minGuests - b.minGuests)) if (guests >= t.minGuests) pct = t.percent;
  return pct;
}
